import * as forge from "node-forge";

export const replaceStringFromMap = (
  str: string,
  map: { [key: string]: string }
) => {
  const matchStr = Object.keys(map).join("|");
  if (!matchStr) return str;
  const regexp = new RegExp(matchStr, "g");
  return str.replace(regexp, (match) => map[match]);
};

/**
 * Ellipsisify truncates the filename (before the extension) to a desired length
 * by keeping the beginning, the end, and the extension of the filename.
 * Examples:
 *
 * With an extension:
 * ellipsisify('asdasdasdasdasdsdf676a87sd678a6sd78a6s78d6a78sd68a.78sd76a87sd6as87da8s7d6a8sd.zip', 20)
 * 'asdasdas…s7d6a8sd.zip'
 *
 * Without an extension:
 * ellipsisify('asdasdasdasdasdsdf676a87sd678a6sd78a6s78d6a78sd68a78sd76a87sd6as87da8s7d6a8sd', 20)
 * 'asdasdas…s7d6a8sd'
 *
 * @param filename
 * @param lengthThreshold
 * @returns
 */
export const ellipsisify = (
  filename: string,
  lengthThreshold: number = 20
): string => {
  if (filename.length < lengthThreshold) return filename;
  const sideLength = Math.floor((lengthThreshold - 3) * 0.5);
  const filenamePieces = filename.split(".");

  let extension;
  let filenameBeforeExtension;

  if (filenamePieces.length > 1) {
    extension = filenamePieces.pop();
    filenameBeforeExtension = filenamePieces
      .slice(0, filenamePieces.length)
      .join(".");
  } else {
    extension = "";
    filenameBeforeExtension = filenamePieces[0];
  }
  const firstLetters = filenameBeforeExtension.substring(0, sideLength);
  const lastLetters = filenameBeforeExtension.substring(
    filenameBeforeExtension.length - sideLength
  );
  const truncatedFilename = `${firstLetters}…${lastLetters}`;
  if (extension) {
    return `${truncatedFilename}.${extension}`;
  } else {
    return truncatedFilename;
  }
};

export const camelCaseToSnakeCase = (str: string) =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

export const normalizeEmail = (email: string) => email.trim().toLowerCase();

export const isNormalizedEmailIncluded = (
  emails: (string | null | undefined)[],
  currentEmail: string
) =>
  emails.some(
    (email) => normalizeEmail(email || "") === normalizeEmail(currentEmail)
  );

export const computeCacheKeyString = (key: unknown): string => {
  const cacheKeyHashed = (
    digest: forge.md.sha256.MessageDigest,
    key: unknown
  ) => {
    switch (typeof key) {
      case "string": {
        digest.update(key);
        return;
      }
      case "boolean": {
        digest.update("" + key);
        return;
      }
      case "number": {
        digest.update("" + key);
        return;
      }
      case "object": {
        if (Array.isArray(key)) {
          for (const element of key) {
            cacheKeyHashed(digest, element);
          }
        }
        for (const field of Object.getOwnPropertyNames(key).sort()) {
          cacheKeyHashed(digest, key[field]);
        }
        return;
      }
      default: {
        throw new Error(`Unexpected nested key type ${typeof key}`);
      }
    }
  };
  if (typeof key === "string") {
    return key;
  }
  const digest = forge.md.sha256.create();
  cacheKeyHashed(digest, key);
  return digest.digest().toHex();
};
